import type { OverviewDatum, OverviewLineData } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';

import { hasAntibioticTarget } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import type { GetGroup, L2Target } from '@resistapp/common/assays';
import { antibioticL2Targets } from '@resistapp/common/assays';
import { calculateNormalizationFactors, summariseWaterVolumeCompleteness } from '@resistapp/common/normalisation-utils';
import { isWaterType, SampleType } from '@resistapp/common/sampling-point-types';
import { getAdminAreaKey } from '@resistapp/common/sampling-points';
import { AdminArea, AdminLevelKey, FullSample, NormalisationMode, ProcessMode } from '@resistapp/common/types';
import { chain, intersection, isNil, keys, type Dictionary } from 'lodash';
import type { LngLatBoundsLike, LngLatLike, MapboxMap } from 'react-map-gl';
import { useOverviewContext } from './use-overview-context';

export function getCountryAdminLevelIntersection(flatSamples: FullSample[]) {
  // TODO: should we include the admin level intersection in this function (?)
  // TODO: support samples accross countries, and with mixed admin levels available
  const countries = chain(flatSamples)
    .map(s => s.samplingPoint.country)
    .uniq()
    .value();

  // Ensure that we only use admin levels that are available for all samples
  // Rural samples might be lacking deeper admin levels that are present in big cities.
  // Eg. sample 36381 (-7.9734244,112.6308338) has levels 1-5 whereas sample 36369 (-6.1982399,106.8451211) has levels 1-9.

  const adminLevels =
    countries.length !== 1
      ? undefined
      : chain(flatSamples)
          .map(s => s.samplingPoint.adminLevels)
          .map(levels => keys(levels || {}))
          .reduce((previous, current) => intersection(previous, current))
          .map(level => +level)
          .value();

  const country = countries.length === 1 ? countries[0] : undefined;
  return { country, adminLevels };
}

export const GLOBAL_NAME = 'Global';
const GLOBAL_SAMPLING_POINT_ID = -99999999;
export function getGlobalOverviewDatum(): OverviewDatum {
  return {
    samplingPoint: {
      type: SampleType.OTHER,
      name: GLOBAL_NAME,
      id: GLOBAL_SAMPLING_POINT_ID,
      subtype: null,
      adminLevels: { 1: { name: GLOBAL_NAME, level: 1 } },
      lat: null,
      lon: null,
      inferredLat: null,
      inferredLon: null,
      country: null,
      region: null,
      city: null,
    },

    beforeAbundances: [],
    afterAbundances: [],

    date: '',
    label: '',
    uniqueDates: [],
  };
}

export function determineZoomableLevels(
  mapDataByLevel: (Dictionary<OverviewDatum[]> & { null?: OverviewDatum[] }) | undefined,
  selectedCountry?: string,
): number[] {
  if (!mapDataByLevel) {
    return [];
  }
  if (!mapDataByLevel['null']) {
    throw new Error('Expected null level to exist');
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (mapDataByLevel['1'] && mapDataByLevel['1'].length > 1) {
    throw new Error('Expected maximum only one site on level 1');
  }

  const numSites = mapDataByLevel['null'].length;

  // First filter data by country if one is selected
  const filteredByCountry = chain(mapDataByLevel)
    .mapValues(levelAreas =>
      selectedCountry ? levelAreas.filter(area => area.samplingPoint.country === selectedCountry) : levelAreas,
    )
    .value();

  const ret = chain(filteredByCountry)
    .toPairs()
    // Consider non-aggregated site level as the deepest level
    .sortBy(([level]) => (isNil(level) ? 16 : +level))
    .filter(([_, levelAreas]) => levelAreas.length > 0)
    .filter(
      // Get rid of weird corner cases where a level has less areas than its upper level
      ([_, levelAreas], pairIndex, allPairs) => !pairIndex || levelAreas.length >= allPairs[pairIndex - 1][1].length,
    )
    .filter(([adminLevel, levelAreas], pairIndex, allPairs) => {
      // Skip useless levels where the next level doesn't have any more areas (note: expects that null level still exists)
      // Exception with countries, which we handle with detecting is adminLevel 2
      return (
        (!selectedCountry && adminLevel === '2' && levelAreas.length > 1) ||
        (pairIndex < allPairs.length - 1 && allPairs[pairIndex + 1][1].length > levelAreas.length)
      );
    })
    // Ensure that some area on each level has more than one data point
    // Also drops special case null site level as the last operation
    .filter(([_, levelAreas]) => levelAreas.length < numSites)
    .map(([level]) => +level)
    .value();

  return ret;
}

export function useSelectedSiteDatumOrThrow() {
  const { selectedSiteDatum } = useOverviewContext();
  if (!selectedSiteDatum) {
    throw new Error('Expected site view to be active');
  }
  return selectedSiteDatum;
}

export function getAllSitesInAdminArea(adminArea: AdminArea, mapData: OverviewDatum[] | undefined) {
  return mapData?.filter(datum => {
    const key = String(adminArea.level) as AdminLevelKey;
    const localAdminArea = datum.samplingPoint.adminLevels?.[key];
    return localAdminArea && getAdminAreaKey(localAdminArea) === getAdminAreaKey(adminArea);
  });
}

export const defaultAvailableNormalisationModes = [NormalisationMode.TEN_UL_DILUTED_DNA_16S, NormalisationMode.SIXTEEN_S];
export function shouldSuppressVolumeModesForProject(samples: FullSample[] | undefined): boolean {
  if (!samples?.length) {
    return false;
  }

  const summary = summariseWaterVolumeCompleteness(
    samples.map(sample => ({
      sampleType: sample.samplingPoint.type,
      filteredVolume: sample.filteredVolume,
      auxExtractionFilteredVolumeMl: sample.auxExtractionFilteredVolumeMl,
    })),
  );
  return summary.isMixed;
}

type DetermineAvailableNormalisationModesOptions = {
  forceSuppressVolumeModes?: boolean;
};

export function determineAvailableNormalisationModes(
  samples: FullSample[] | undefined,
  envIds?: number[],
  options?: DetermineAvailableNormalisationModesOptions,
): NormalisationMode[] {
  const envIdsSet = envIds ? new Set(envIds) : undefined;
  const relevantSamples = !envIdsSet ? samples : samples?.filter(s => envIdsSet.has(s.samplingPoint.id));
  if (!relevantSamples?.length) {
    return defaultAvailableNormalisationModes;
  }
  const waterSamples = relevantSamples.filter(sample => isWaterType(sample.samplingPoint.type as SampleType));

  const waterSummary = summariseWaterVolumeCompleteness(
    relevantSamples.map(sample => ({
      sampleType: sample.samplingPoint.type,
      filteredVolume: sample.filteredVolume,
      auxExtractionFilteredVolumeMl: sample.auxExtractionFilteredVolumeMl,
    })),
  );
  const hasWaterSamples = waterSummary.applicableCount > 0;
  const hasCompleteWaterVolume =
    hasWaterSamples &&
    !waterSummary.isMixed &&
    !options?.forceSuppressVolumeModes &&
    waterSummary.withVolumeCount === waterSummary.applicableCount;

  const hasVolume =
    hasCompleteWaterVolume &&
    waterSamples.every(sample => !isNil(calculateNormalizationFactors(sample).volumeNormalisationFactor));
  const hasTime =
    hasCompleteWaterVolume &&
    waterSamples.every(sample => !isNil(calculateNormalizationFactors(sample).flowNormlisationFactor));
  const hasSS =
    hasCompleteWaterVolume &&
    waterSamples.every(sample => !isNil(calculateNormalizationFactors(sample).suspendedSolidsNormlisationFactor));
  const hasBOD =
    hasCompleteWaterVolume &&
    waterSamples.every(sample => !isNil(calculateNormalizationFactors(sample).bodNormlisationFactor));

  // Note: priority order
  return chain([
    hasVolume && NormalisationMode.LITRE_16S,
    hasSS && NormalisationMode.MG_SS,
    hasTime && NormalisationMode.HOUR,
    NormalisationMode.TEN_UL_DILUTED_DNA_16S,
    NormalisationMode.SIXTEEN_S,
    hasBOD && NormalisationMode.MG_BOD,
  ])
    .filter(Boolean)
    .value() as NormalisationMode[];
}

export function zoomAndCenterMap(
  mapInstance: MapboxMap,
  zoomOrBounds: { zoom: number; center: LngLatLike } | { bounds: LngLatBoundsLike },
) {
  const MIN_ZOOM = 13;
  if ('zoom' in zoomOrBounds) {
    mapInstance.flyTo({
      zoom: Math.min(MIN_ZOOM, zoomOrBounds.zoom),
      center: zoomOrBounds.center,
      padding: 120,
    });
  } else if ('bounds' in zoomOrBounds) {
    const cameraForBounds = mapInstance.cameraForBounds(zoomOrBounds.bounds);
    mapInstance.flyTo({
      zoom: Math.min(MIN_ZOOM, cameraForBounds?.zoom || MIN_ZOOM),
      center: cameraForBounds?.center,
      padding: 120,
    });
  }
}

export function getSelectedSite({
  queryFilters,
  mapData,
  supportedSamples,
}: {
  queryFilters: { filters: { selectedSamplingPointNamesOrdered: string[] } };
  mapData: OverviewDatum[] | undefined;
  supportedSamples: FullSample[] | undefined;
}) {
  const uniqSelectedLocations = chain(supportedSamples)
    .filter(s => queryFilters.filters.selectedSamplingPointNamesOrdered.includes(s.samplingPoint.name))
    .map(s => `${s.samplingPoint.lat},${s.samplingPoint.lon}`)
    .uniq()
    .value();

  // There is only one site (selected 1 or 2 envs, we don't support more smplPnt selections currently)
  const singleSiteSelected =
    queryFilters.filters.selectedSamplingPointNamesOrdered.length === 1 ||
    (queryFilters.filters.selectedSamplingPointNamesOrdered.length === 2 && uniqSelectedLocations.length === 1);

  const selectedSmplPntNames = singleSiteSelected ? queryFilters.filters.selectedSamplingPointNamesOrdered : [];

  return selectedSmplPntNames.length
    ? mapData?.filter(d =>
        selectedSmplPntNames.some(
          envName =>
            'originalSamplingPointNames' in d.samplingPoint &&
            d.samplingPoint.originalSamplingPointNames?.includes(envName),
        ),
      )[0]
    : undefined;
}

export function getAnalysedGroupsAndAll(trendData: OverviewLineData | undefined, getGroup: GetGroup) {
  const datum = trendData?.flat().find(d => !!(d.beforeAbundances || d.afterAbundances)?.length);
  const processModeToUseForDatum = datum?.beforeAbundances?.length ? ProcessMode.BEFORE : ProcessMode.AFTER;
  const analyzedAntibioticGroups = antibioticL2Targets.filter(antibiotic => {
    // This needs to only calculate the analysed antibiotics for one data, as the data should be unified so that the
    // first one has the same as the same antibiotics as the other datas.
    return datum && hasAntibioticTarget(datum, antibiotic, processModeToUseForDatum, getGroup);
  });
  return analyzedAntibioticGroups.length > 0
    ? (['All antibiotics', ...analyzedAntibioticGroups] satisfies Array<L2Target | 'All antibiotics'>)
    : ([] satisfies Array<L2Target | 'All antibiotics'>);
}

export function getEffectiveSiteProcessMode(
  overviewDatum: OverviewDatum | undefined,
  defaultProcessMode: ProcessMode,
): ProcessMode {
  return !overviewDatum
    ? defaultProcessMode
    : defaultProcessMode === ProcessMode.AFTER &&
        !overviewDatum.afterAbundances?.length &&
        overviewDatum.beforeAbundances?.length
      ? ProcessMode.BEFORE
      : defaultProcessMode === ProcessMode.BEFORE &&
          !overviewDatum.beforeAbundances?.length &&
          overviewDatum.afterAbundances?.length
        ? ProcessMode.AFTER
        : defaultProcessMode;
}
